'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TeamMember {
  id: string;
  name: string;
  role: string;
  avatar: string;
  deliveries: number;
  onTimeRate: number;
  customerRating: number;
  activeShipments: number;
  status: 'active' | 'offline' | 'on-break';
  hoursWorked: number;
}

const mockTeamMembers: TeamMember[] = [
  {
    id: '1',
    name: 'John Smith',
    role: 'Senior Driver',
    avatar: 'JS',
    deliveries: 342,
    onTimeRate: 98.5,
    customerRating: 4.9,
    activeShipments: 5,
    status: 'active',
    hoursWorked: 156,
  },
  {
    id: '2',
    name: 'Maria Garcia',
    role: 'Driver',
    avatar: 'MG',
    deliveries: 298,
    onTimeRate: 96.2,
    customerRating: 4.8,
    activeShipments: 4,
    status: 'active',
    hoursWorked: 148,
  },
  {
    id: '3',
    name: 'David Lee',
    role: 'Driver',
    avatar: 'DL',
    deliveries: 276,
    onTimeRate: 94.8,
    customerRating: 4.7,
    activeShipments: 3,
    status: 'on-break',
    hoursWorked: 142,
  },
  {
    id: '4',
    name: 'Sarah Wilson',
    role: 'Junior Driver',
    avatar: 'SW',
    deliveries: 189,
    onTimeRate: 92.3,
    customerRating: 4.6,
    activeShipments: 2,
    status: 'active',
    hoursWorked: 98,
  },
  {
    id: '5',
    name: 'Michael Brown',
    role: 'Driver',
    avatar: 'MB',
    deliveries: 245,
    onTimeRate: 95.7,
    customerRating: 4.8,
    activeShipments: 0,
    status: 'offline',
    hoursWorked: 132,
  },
];

export default function TeamPerformance() {
  const [teamMembers] = useState<TeamMember[]>(mockTeamMembers);
  const [sortBy, setSortBy] = useState<string>('deliveries');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filteredMembers = teamMembers
    .filter((member) => filterStatus === 'all' || member.status === filterStatus)
    .sort((a, b) => {
      switch (sortBy) {
        case 'deliveries':
          return b.deliveries - a.deliveries;
        case 'onTimeRate':
          return b.onTimeRate - a.onTimeRate;
        case 'rating':
          return b.customerRating - a.customerRating;
        case 'hours':
          return b.hoursWorked - a.hoursWorked;
        default:
          return 0;
      }
    });

  const totalDeliveries = teamMembers.reduce((sum, m) => sum + m.deliveries, 0);
  const avgOnTimeRate = teamMembers.reduce((sum, m) => sum + m.onTimeRate, 0) / teamMembers.length;
  const avgRating = teamMembers.reduce((sum, m) => sum + m.customerRating, 0) / teamMembers.length;
  const activeMembers = teamMembers.filter((m) => m.status === 'active').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'on-break':
        return 'text-accent bg-accent/10';
      case 'offline':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="space-y-6">
      {/* Team KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png" size={24} className="text-primary" />
            <h3 className="text-sm font-medium text-muted-foreground">Total Deliveries</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{totalDeliveries.toLocaleString()}</p>
          <p className="text-sm text-success mt-1">+12% this month</p>
        </div>
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="ClockIcon" size={24} className="text-secondary" />
            <h3 className="text-sm font-medium text-muted-foreground">Avg On-Time Rate</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{avgOnTimeRate.toFixed(1)}%</p>
          <p className="text-sm text-success mt-1">+2.3% this month</p>
        </div>
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="StarIcon" size={24} className="text-accent" />
            <h3 className="text-sm font-medium text-muted-foreground">Avg Rating</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{avgRating.toFixed(1)}</p>
          <p className="text-sm text-success mt-1">+0.2 this month</p>
        </div>
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="UserGroupIcon" size={24} className="text-success" />
            <h3 className="text-sm font-medium text-muted-foreground">Active Team</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{activeMembers}/{teamMembers.length}</p>
          <p className="text-sm text-muted-foreground mt-1">Currently working</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Sort By</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="deliveries">Total Deliveries</option>
              <option value="onTimeRate">On-Time Rate</option>
              <option value="rating">Customer Rating</option>
              <option value="hours">Hours Worked</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="on-break">On Break</option>
              <option value="offline">Offline</option>
            </select>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      <div className="grid grid-cols-1 gap-6">
        {filteredMembers.map((member, index) => (
          <div key={member.id} className="bg-card rounded-xl p-6 shadow-card border border-border hover:shadow-lg transition-shadow">
            <div className="flex items-start gap-6">
              {/* Avatar and Rank */}
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl">
                  {member.avatar}
                </div>
                <span className="text-xs font-medium text-muted-foreground">#{index + 1}</span>
              </div>

              {/* Member Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-1">{member.name}</h3>
                    <p className="text-sm text-muted-foreground">{member.role}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(member.status)}`}>
                    {member.status.replace('-', ' ')}
                  </span>
                </div>

                {/* Performance Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Deliveries</p>
                    <p className="text-2xl font-bold text-foreground">{member.deliveries}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">On-Time Rate</p>
                    <p className="text-2xl font-bold text-foreground">{member.onTimeRate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Rating</p>
                    <div className="flex items-center gap-1">
                      <Icon name="StarIcon" size={20} className="text-accent" />
                      <p className="text-2xl font-bold text-foreground">{member.customerRating}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Active</p>
                    <p className="text-2xl font-bold text-foreground">{member.activeShipments}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Hours</p>
                    <p className="text-2xl font-bold text-foreground">{member.hoursWorked}h</p>
                  </div>
                </div>

                {/* Progress Bars */}
                <div className="mt-4 space-y-2">
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>On-Time Performance</span>
                      <span>{member.onTimeRate}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary rounded-full h-2 transition-all"
                        style={{ width: `${member.onTimeRate}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-muted-foreground mb-1">
                      <span>Customer Satisfaction</span>
                      <span>{((member.customerRating / 5) * 100).toFixed(0)}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-accent rounded-full h-2 transition-all"
                        style={{ width: `${(member.customerRating / 5) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredMembers.length === 0 && (
        <div className="bg-card rounded-xl p-12 shadow-card border border-border text-center">
          <Icon name="UserGroupIcon" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No team members found matching your filters</p>
        </div>
      )}
    </div>
  );
}
